/**
 * The `Project` record. SPEC-0007.
 *
 * Before this, "project" was not a persistent thing in Picklii — it existed
 * only as rows inside whichever run last touched its `project_id`. This is a
 * small, current-state document per project, upserted as a side effect of
 * completing a run (`apps/api/src/projects.ts`), sibling to `mappingProfiles`
 * in Firestore (`tech-stack.md` §7) rather than a growing history — see
 * SPEC-0007's Scope for why this does not itself trigger ADR-0004's
 * cross-run-rollup revisit trigger.
 */
import { z } from 'zod';
/**
 * Identity is `project_id` alone — decided (product owner, 2026-09-23,
 * SPEC-0007 Open questions), matching how `reconcile()` already joins.
 * `clientName` below is denormalized from whichever run most recently
 * touched the project and can drift if a customer's own naming is
 * inconsistent across uploads — an accepted, minor cost of that decision.
 */
export const projectSchema = z.object({
    projectId: z.string(),
    /** Denormalized from the most recent run that touched this project. */
    clientName: z.string(),
    /** ISO-8601, or `null` if never set or never agreed on (see the
     * mismatch-flagging rule in SPEC-0007's Recommendation §2). */
    goLiveDate: z.string().nullable(),
    endDate: z.string().nullable(),
    /** True exactly while the field above is sitting on a stale or absent
     * value because the sold-hours rows disagreed and nobody has resolved it
     * yet — cleared the moment a run's rows agree, or a human sets the field
     * directly (SPEC-0007 Recommendation §2's "recommended, not yet
     * confirmed" resolution path). Lets the Dashboard flag the card without
     * re-deriving it from run history on every read. */
    goLiveDateDisputed: z.boolean(),
    endDateDisputed: z.boolean(),
    archived: z.boolean(),
    archivedAt: z.string().nullable(),
    // Denormalized from the latest run touching this project, so the
    // Dashboard renders a card grid without re-reading every run's findings
    // subcollection for every project on every page load. Field names and the
    // exact/estimated split match `RunSummary`/`Finding` in `@picklii/recon`
    // exactly — no new money or severity representation invented here.
    latestRunId: z.string(),
    latestFindingType: z
        .enum(['PROJECT_MISSING_IN_PSA', 'ROLE_MISSING_IN_PSA', 'ROLE_MISSING_IN_SOLD', 'HOURS_MISMATCH'])
        .nullable(),
    latestSeverityBand: z.enum(['Critical', 'High', 'Medium', 'Low']).nullable(),
    latestExactExposureCents: z.number().int(),
    latestEstimatedExposureCents: z.number().int(),
    /** From the worst finding, same as the four fields above. `null` means no
     * budget to burn against — never coerced to 0%. SPEC-0007's own metrics
     * table names this as shown per card; added here after the fact because
     * the first draft of this schema omitted it. */
    latestBurnPct: z.number().nullable(),
    /** Signed. `null` exactly when there is no finding to take it from —
     * never 0, which would read as "on budget" rather than "nothing to show." */
    latestDeltaHours: z.number().nullable(),
    hasUnmatchedRoles: z.boolean(),
});
//# sourceMappingURL=projects.js.map